import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  MapPin, Check, X, Loader2, Eye, CheckCircle, User 
} from 'lucide-react';
import { formatDate, formatCurrency, getListingTypeLabel } from '../../lib/utils';
import { toast } from 'sonner';

const defaultImage = 'https://images.unsplash.com/photo-1563116128-261f46267cac?w=400';

export default function AdminListings() {
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || 'submitted';
  
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialStatus);
  const [selectedListing, setSelectedListing] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchListings();
  }, [activeTab]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getListings({ status: activeTab !== 'all' ? activeTab : undefined });
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (listingId, status) => {
    setProcessing(true);
    try {
      await adminApi.updateListingStatus(listingId, status);
      toast.success(`Listing ${status}`);
      fetchListings();
      setSelectedListing(null);
    } catch (error) {
      toast.error('Failed to update listing');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-farmhouse-stone/20 text-farmhouse-muted',
      submitted: 'bg-farmhouse-gold/20 text-farmhouse-gold',
      approved: 'bg-farmhouse-moss/20 text-green-400',
      rejected: 'bg-farmhouse-clay/20 text-farmhouse-clay',
    };
    return colors[status] || colors.draft;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-farmhouse-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-farmhouse-saddle animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-farmhouse-charcoal pt-8 pb-16" data-testid="admin-listings-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-4xl font-semibold text-farmhouse-cream mb-8">
          Manage Listings
        </h1>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-farmhouse-paper border border-farmhouse-subtle mb-6">
            <TabsTrigger 
              value="submitted"
              className="data-[state=active]:bg-farmhouse-saddle data-[state=active]:text-white"
            >
              Pending Review
            </TabsTrigger>
            <TabsTrigger 
              value="approved"
              className="data-[state=active]:bg-farmhouse-saddle data-[state=active]:text-white"
            >
              Approved
            </TabsTrigger>
            <TabsTrigger 
              value="rejected"
              className="data-[state=active]:bg-farmhouse-saddle data-[state=active]:text-white"
            >
              Rejected
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {listings.length > 0 ? (
              <div className="space-y-4">
                {listings.map((listing) => (
                  <div
                    key={listing.id}
                    className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-4 cursor-pointer card-hover"
                    onClick={() => setSelectedListing(listing)}
                    data-testid={`admin-listing-${listing.id}`}
                  >
                    <div className="flex flex-col md:flex-row gap-4">
                      <img
                        src={listing.photos?.[0] || defaultImage}
                        alt={listing.title}
                        className="w-full md:w-32 h-32 object-cover rounded-md"
                        onError={(e) => { e.target.src = defaultImage; }}
                      />
                      
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={`${getStatusBadge(listing.status)} border-0 capitalize`}>
                                {listing.status}
                              </Badge>
                              <Badge variant="outline" className="border-farmhouse-subtle text-farmhouse-muted">
                                {getListingTypeLabel(listing.listing_type)}
                              </Badge>
                            </div>
                            <h3 className="font-serif text-lg font-semibold text-farmhouse-cream">
                              {listing.title}
                            </h3>
                          </div>
                          <p className="text-xl font-semibold text-farmhouse-cream">
                            {formatCurrency(listing.pricing?.nightly || 0)}/night
                          </p>
                        </div>

                        <div className="flex items-center gap-2 text-farmhouse-muted text-sm mt-2">
                          <MapPin className="w-4 h-4" />
                          <span>{listing.location?.city}, {listing.location?.state}</span>
                        </div>

                        <div className="flex items-center gap-2 text-farmhouse-muted text-sm mt-1">
                          <User className="w-4 h-4" />
                          <span>Host: {listing.host?.name || 'Unknown'} ({listing.host?.email})</span>
                        </div>

                        <p className="text-farmhouse-muted text-sm mt-2">
                          Submitted: {formatDate(listing.created_at)}
                        </p>

                        {listing.status === 'submitted' && (
                          <div className="flex gap-2 mt-4">
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateStatus(listing.id, 'approved');
                              }}
                              className="bg-farmhouse-moss hover:bg-farmhouse-moss/80 text-white"
                              data-testid="approve-listing"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateStatus(listing.id, 'rejected');
                              }}
                              className="border-farmhouse-clay text-farmhouse-clay hover:bg-farmhouse-clay/10"
                              data-testid="reject-listing"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-farmhouse-paper border border-farmhouse-subtle rounded-md">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-farmhouse-subtle" />
                <h3 className="font-serif text-xl font-semibold text-farmhouse-cream mb-2">
                  No {activeTab} listings
                </h3>
                <p className="text-farmhouse-muted">
                  {activeTab === 'submitted' ? 'All caught up! No listings pending review.' : `No ${activeTab} listings found.`}
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Listing Detail Dialog */}
        <Dialog open={!!selectedListing} onOpenChange={(open) => !open && setSelectedListing(null)}>
          <DialogContent className="bg-farmhouse-paper border-farmhouse-subtle max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedListing && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl text-farmhouse-cream">
                    Listing Details
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  {/* Images */}
                  {selectedListing.photos?.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {selectedListing.photos.slice(0, 3).map((photo, idx) => (
                        <img
                          key={idx}
                          src={photo}
                          alt={`Photo ${idx + 1}`}
                          className="w-full aspect-square object-cover rounded-md"
                          onError={(e) => { e.target.src = defaultImage; }}
                        />
                      ))}
                    </div>
                  )}

                  <div>
                    <h3 className="font-serif text-xl font-semibold text-farmhouse-cream">
                      {selectedListing.title}
                    </h3>
                    <p className="text-farmhouse-muted mt-2">{selectedListing.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 p-4 bg-farmhouse-charcoal rounded-md">
                    <div>
                      <p className="text-farmhouse-muted text-sm">Location</p>
                      <p className="text-farmhouse-cream">
                        {selectedListing.location?.address && `${selectedListing.location.address}, `}
                        {selectedListing.location?.city}, {selectedListing.location?.state} {selectedListing.location?.zip_code}
                      </p>
                    </div>
                    <div>
                      <p className="text-farmhouse-muted text-sm">Type</p>
                      <p className="text-farmhouse-cream capitalize">{getListingTypeLabel(selectedListing.listing_type)}</p>
                    </div>
                    <div>
                      <p className="text-farmhouse-muted text-sm">Capacity</p>
                      <p className="text-farmhouse-cream">{selectedListing.capacity}</p>
                    </div>
                    <div>
                      <p className="text-farmhouse-muted text-sm">Pricing</p>
                      <p className="text-farmhouse-cream">{formatCurrency(selectedListing.pricing?.nightly)}/night</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-farmhouse-muted text-sm mb-2">Host Information</p>
                    <div className="flex items-center gap-3 p-3 bg-farmhouse-charcoal rounded-md">
                      <div className="avatar bg-farmhouse-saddle text-white">
                        {selectedListing.host?.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="text-farmhouse-cream">{selectedListing.host?.name}</p>
                        <p className="text-farmhouse-muted text-sm">{selectedListing.host?.email}</p>
                      </div>
                    </div>
                  </div>

                  {selectedListing.status === 'submitted' && (
                    <div className="flex gap-3 pt-4 border-t border-farmhouse-subtle">
                      <Button
                        onClick={() => handleUpdateStatus(selectedListing.id, 'approved')}
                        className="flex-1 bg-farmhouse-moss hover:bg-farmhouse-moss/80 text-white"
                        disabled={processing}
                      >
                        {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Approve Listing
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleUpdateStatus(selectedListing.id, 'rejected')}
                        variant="outline"
                        className="flex-1 border-farmhouse-clay text-farmhouse-clay hover:bg-farmhouse-clay/10"
                        disabled={processing}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
